import React from "react";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  InputLabel,
  Box,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { formatGameFormat, formatPokemonName } from "../../utils/formatHelpers";

// Shared styles for all Select/TextField inputs in the dark filter bar
const inputSx = {
  color: "white",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.5)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#24cc9f" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "white",
    "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
    "&.Mui-focused fieldset": { borderColor: "#24cc9f" },
    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#24cc9f" },
};

const labelSx = { color: "rgba(255,255,255,0.7)" };

// Dropdown paper for Autocomplete (matches dark theme)
const autocompletePaperSx = {
  backgroundColor: "#1c1ab0",
  "& .MuiAutocomplete-option": {
    color: "white",
    '&[aria-selected="true"]': { backgroundColor: "rgba(36,204,159,0.25)" },
    "&.Mui-focused": { backgroundColor: "rgba(255,255,255,0.1)" },
  },
  "& .MuiAutocomplete-noOptions": { color: "rgba(255,255,255,0.5)" },
};

/**
 * Reusable filter bar for public/saved games pages.
 */
const GameFilters = ({
  sortBy,
  onSortByChange,
  playerFilter,
  onPlayerFilterChange,
  ratingFilter,
  onRatingFilterChange,
  dateFilter,
  onDateFilterChange,
  formatFilter,
  onFormatFilterChange,
  availableFormats = [],
  isLoadingFormats = false,
  pokemonFilter,
  onPokemonFilterChange,
  availablePokemon = [],
  isLoadingPokemon = false,
  showSaved,
  onShowSavedChange,
  showSavedFilter = false,
  onApply,
  onReset,
  compact = false,
}) => {
  // Normalize typed text → internal pokemon name (e.g. "Landorus Therian" → "landorus-therian")
  const normalizePokemonInput = (text) =>
    text.trim().toLowerCase().replace(/\s+/g, "-");

  const handlePokemonChange = (_, newValue) => {
    if (!newValue) {
      onPokemonFilterChange("");
    } else if (typeof newValue === "string") {
      onPokemonFilterChange(normalizePokemonInput(newValue));
    } else {
      onPokemonFilterChange(newValue.name);
    }
  };

  // Find the pokemon object matching the current filter value (for controlled value)
  const selectedPokemon =
    availablePokemon.find((p) => p.name === pokemonFilter) || null;

  // How many extra filters are in row 2 (format + pokemon + optional showSaved)
  const extraFiltersCount = 2 + (showSavedFilter ? 1 : 0);
  const buttonsMd = 12 - extraFiltersCount * 3; // 3 or 6

  return (
    <Box
      sx={{
        mb: compact ? 2 : 3,
        p: 2,
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: "#221FC7",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      <Grid container spacing={2}>
        {/* Row 1: Sort, Player, Rating, Date */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="sort-by-label" sx={labelSx}>
              Sort by
            </InputLabel>
            <Select
              labelId="sort-by-label"
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value)}
              label="Sort by"
              sx={inputSx}
            >
              <MenuItem value="date DESC">Date Descending</MenuItem>
              <MenuItem value="date ASC">Date Ascending</MenuItem>
              <MenuItem value="rating DESC">Rating Descending</MenuItem>
              <MenuItem value="rating ASC">Rating Ascending</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Search by player"
            value={playerFilter}
            onChange={(e) => onPlayerFilterChange(e.target.value)}
            size="small"
            variant="outlined"
            fullWidth
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="rating-label" sx={labelSx}>
              Rating
            </InputLabel>
            <Select
              labelId="rating-label"
              value={ratingFilter}
              onChange={(e) => onRatingFilterChange(e.target.value)}
              label="Rating"
              sx={inputSx}
            >
              <MenuItem value="all">All Ratings</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
              <MenuItem value="1200+">&gt;1200</MenuItem>
              <MenuItem value="1500+">&gt;1500</MenuItem>
              <MenuItem value="1800+">&gt;1800</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="date-label" sx={labelSx}>
              Date
            </InputLabel>
            <Select
              labelId="date-label"
              value={dateFilter}
              onChange={(e) => onDateFilterChange(e.target.value)}
              label="Date"
              sx={inputSx}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="week">Last Week</MenuItem>
              <MenuItem value="month">Last Month</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Row 2: Format, Pokémon, Show Saved (optional), Buttons */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="format-label" sx={labelSx}>
              Format
            </InputLabel>
            <Select
              labelId="format-label"
              value={formatFilter}
              onChange={(e) => onFormatFilterChange(e.target.value)}
              label="Format"
              disabled={isLoadingFormats}
              sx={inputSx}
            >
              <MenuItem value="all">All Formats</MenuItem>
              {isLoadingFormats ? (
                <MenuItem disabled>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <span>Loading...</span>
                  </Box>
                </MenuItem>
              ) : (
                availableFormats.map((format) => (
                  <MenuItem key={format} value={format}>
                    {formatGameFormat(format)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={availablePokemon}
            getOptionLabel={(opt) =>
              typeof opt === "string"
                ? formatPokemonName(opt)
                : (opt.displayName ?? formatPokemonName(opt.name))
            }
            isOptionEqualToValue={(opt, val) =>
              opt.name === (typeof val === "string" ? val : val?.name)
            }
            value={selectedPokemon}
            onChange={handlePokemonChange}
            loading={isLoadingPokemon}
            freeSolo
            clearOnEscape
            size="small"
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.toLowerCase().replace(/\s+/g, "-");
              return opts
                .filter((p) => p.name.includes(q) || p.displayName?.toLowerCase().includes(inputValue.toLowerCase()))
                .slice(0, 25);
            }}
            slotProps={{
              paper: { sx: autocompletePaperSx },
              clearIndicator: { sx: { color: "rgba(255,255,255,0.7)" } },
              popupIndicator: { sx: { color: "rgba(255,255,255,0.7)" } },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pokémon"
                size="small"
                sx={textFieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingPokemon ? <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.5)" }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {showSavedFilter && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="show-saved-label" sx={labelSx}>
                Show
              </InputLabel>
              <Select
                labelId="show-saved-label"
                value={showSaved}
                onChange={(e) => onShowSavedChange(e.target.value)}
                label="Show"
                sx={inputSx}
              >
                <MenuItem value="all">All Replays</MenuItem>
                <MenuItem value="unsaved">Unsaved Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Buttons */}
        <Grid
          item
          xs={12}
          sm={12}
          md={buttonsMd}
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            justifyContent: { xs: "stretch", md: "flex-end" },
          }}
        >
          <Button
            variant="contained"
            onClick={onApply}
            sx={{
              flex: { xs: 1, md: "0 1 120px" },
              minWidth: "100px",
              backgroundColor: "#24cc9f",
              color: "white",
              fontWeight: "bold",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#1fa885",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(36,204,159,0.4)",
              },
            }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            onClick={onReset}
            sx={{
              flex: { xs: 1, md: "0 1 120px" },
              minWidth: "100px",
              borderColor: "rgba(255,255,255,0.5)",
              color: "white",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255,255,255,0.1)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Reset
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GameFilters;
